import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion, useReducedMotion } from 'motion/react';
import { ChevronDown } from 'lucide-react';
import { useUiStore } from '@/store/useUiStore';
import Reveal from '@/components/Reveal';
import './Ch3Architecture.css';

interface Layer {
  name: string;
  items: string[];
}
interface Model {
  n: string;
  by: string;
}
interface Tier {
  key: string;
  name: string;
  param: string;
  capability: string;
  models: Model[];
  isValion?: boolean;
}

/* ---------- 3a · architecture explorer ---------- */
function ArchitectureExplorer() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const layers = t('architecture.layers', { returnObjects: true }) as Layer[];
  const [open, setOpen] = useState(0);

  return (
    <div className="arch-stack">
      {layers.map((layer, i) => {
        const isOpen = open === i;
        return (
          <div key={layer.name} className="arch-layer" data-tone={i < 2 ? 'maroon' : 'navy'} data-open={isOpen}>
            <button className="arch-layer-head" onClick={() => setOpen(isOpen ? -1 : i)} aria-expanded={isOpen}>
              <span className="arch-layer-index">{String(i + 1).padStart(2, '0')}</span>
              <span className="arch-layer-name">{layer.name}</span>
              <ChevronDown className={`arch-chevron${isOpen ? ' arch-chevron--open' : ''}`} size={20} />
            </button>
            <AnimatePresence initial={false}>
              {isOpen && (
                <motion.div
                  className="arch-layer-body"
                  initial={reduce ? false : { height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={reduce ? undefined : { height: 0, opacity: 0 }}
                  transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="arch-items">
                    {layer.items.map((item) => (
                      <span key={item} className="arch-item">
                        {item}
                      </span>
                    ))}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
            {/* data pulse connector between layers */}
            {i < layers.length - 1 && (
              <span className="arch-connector" aria-hidden="true">
                {!reduce &&
                  [0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      className="arch-pulse"
                      initial={{ y: -6, opacity: 0 }}
                      animate={{ y: 22, opacity: [0, 1, 0] }}
                      transition={{ duration: 1.6, delay: d * 0.5, repeat: Infinity, ease: 'easeInOut' }}
                    />
                  ))}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ---------- 3b · benchmark ---------- */
function Benchmark() {
  const { t } = useTranslation();
  const tiers = t('architecture.benchmark.tiers', { returnObjects: true }) as Tier[];

  return (
    <div className="bench">
      {tiers.map((tier, i) => (
        <Reveal key={tier.key} delay={i * 0.1}>
          <div className={`bench-tier${tier.isValion ? ' bench-tier--valion' : ''}`}>
            <div className="bench-tier-meta">
              <h4 className="bench-tier-name">{tier.name}</h4>
              <span className="bench-tier-param">{tier.param}</span>
              <p className="bench-tier-cap">{tier.capability}</p>
            </div>
            <div className="bench-models">
              {tier.models.map((m) => (
                <div key={m.n} className="bench-model">
                  <span className="bench-model-name">{m.n}</span>
                  <span className="bench-model-by">{m.by}</span>
                </div>
              ))}
              {tier.isValion && (
                <div className="bench-model bench-model--valion">
                  <span className="bench-valion-mark">VALION</span>
                  <span className="bench-valion-here">{t('architecture.benchmark.hereLabel')}</span>
                </div>
              )}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

export default function Ch3Architecture() {
  const { t } = useTranslation();
  const setActiveChapter = useUiStore((s) => s.setActiveChapter);

  return (
    <section
      id="architecture"
      className="chapter chapter-arch"
      aria-labelledby="arch-title"
      onMouseEnter={() => setActiveChapter('architecture')}
    >
      <div className="chapter-inner">
        <div className="chapter-head">
          <p className="section-kicker">{t('architecture.kicker')}</p>
          <h2 id="arch-title" className="chapter-title">
            {t('architecture.title')}
          </h2>
          <p className="chapter-lead">{t('architecture.intro')}</p>
        </div>

        <p className="arch-hint">{t('architecture.hint')}</p>
        <ArchitectureExplorer />

        <div className="arch-footer-strip">{t('architecture.footerStrip')}</div>

        {/* Benchmark */}
        <div className="bench-head chapter-head">
          <h3 className="chapter-title">{t('architecture.benchmark.title')}</h3>
          <p className="chapter-lead">{t('architecture.benchmark.subtitle')}</p>
        </div>
        <Benchmark />
      </div>
    </section>
  );
}
